<template>
    <div class="close-header-title no-con">
        <section class="privacy-section">
            <div class="container">
                <div class="main-heading">
                    <h1>COOKIES POLICY OF CHEAP COMFORTS</h1>
                    <p>Last updated: 7 September 2025</p>
                    <p>This Cookie Policy (“Policy”) explains how Cheap Comforts (“Cheap Comforts”, “we”, “us”, “our”)
                        uses cookies and similar technologies on <a href="https://www.cheapcomforts.com"
                            target="_blank">www.cheapcomforts.com</a> and related subdomains (the “Site” or “Platform”).
                        It also sets out your choices, including granular consent controls, regional options, and how to
                        withdraw consent at any time. This Policy forms part of our <a href="/termsofservice"
                            target="_blank">Terms of Service</a> and <a href="/privacypolicy" target="_blank">Privacy
                            Policy</a> and should be read together with them. Capitalized terms used but not defined
                        here have the
                        meanings given in those documents.</p>
                    <p>Cheap Comforts operates a global online marketplace facilitating medium-term property rentals
                        (1–12 months) between Hosts and Guests. We act solely as an intermediary—we do not own or manage
                        properties—and we process payments via Stripe. Our hosting provider is Hostinger and our
                        analytics provider is Google Analytics.</p>
                </div>
                <div class="content-section center">
                    <h2>ARTICLE I: SCOPE, GOVERNING LAW & CONTACT</h2>
                    <h4>1.1 Scope.</h4>
                    <p>This Policy applies to cookies, SDKs, pixels, tags, local storage, and comparable technologies
                        (collectively, “cookies”) on our Site, web apps, and any browser-based interfaces that Cheap
                        Comforts controls. It also describes how our mobile SDKs (if and when used) mirror cookie
                        choices.</p>
                    <h4>1.2 Governing Law; Mandatory Regional Rules.</h4>
                    <p>This Policy is governed by the laws of Spain, without prejudice to mandatory rules in your
                        location (e.g., EU/UK ePrivacy/GDPR consent standards; U.S. state “Do Not Sell/Share” opt-out
                        rules; Brazil LGPD). Where local law requires stricter controls, we apply those controls.</p>
                    <h4>1.3 Contact.</h4>
                    <p>For questions or to exercise rights, contact <a href="mailto:support@cheapcomforts.com"
                            target="_blank">support@cheapcomforts.com</a>. The same address is used for cookie-related
                        complaints. If and when we target users in the United Kingdom, we will appoint a UK
                        representative as required by the UK GDPR; contact details will be provided upon request at this
                        address.</p>
                    <h4>1.4 Updates.</h4>
                    <p>We may update this Policy from time to time. Material changes will be communicated in the banner
                        or via the Cookie Settings panel. We will re-prompt for consent when the purpose, vendors, or
                        categories meaningfully change.</p>

                    <h2>ARTICLE II: WHAT COOKIES & SIMILAR TECHNOLOGIES ARE</h2>
                    <h4>2.1 Cookies.</h4>
                    <p>Small text files placed on your device by a website. They may be session (expire when you close
                        your browser) or persistent (remain until their expiry or deletion), first-party (set by Cheap
                        Comforts) or third-party (set by a provider such as Stripe or Google).</p>
                    <h4>2.2 Similar Technologies.</h4>
                    <p>(1) Local storage / session storage: key-value storage in the browser;</p>
                    <p>(2) Pixels / tags: transparent images or scripts used to measure events;</p>
                    <p>(3) SDKs: mobile application libraries functioning similarly to cookies;</p>
                    <p>(4) Device signals / fingerprinting: collecting device/configuration data to identify a browser
                        or device.</p>
                    <h4>2.3 Treatment Under Law.</h4>
                    <p>Where these technologies are used for analytics, advertising, cross-site tracking, or device
                        recognition, we treat them as tracking requiring prior consent in the EU/UK and as “sale/share”
                        opt-out-eligible in U.S. California-style regimes.</p>

                    <h2>ARTICLE III: OUR CONSENT FRAMEWORK & YOUR CHOICES</h2>
                    <h4>3.1 EU/UK Prior Consent (Opt-In).</h4>
                    <p>In the EU/UK (and any region we designate as “strict consent”), no non-essential cookies
                        (Analytics/Performance, Advertising/Marketing) are placed until you opt in. You will see a
                        first-layer banner with:</p>
                    <p>“Accept all” (enable all optional categories),</p>
                    <p>“Reject all” (disable all optional categories), and</p>
                    <p>“Save choices” (apply your per-category selections).</p>
                    <h4>3.2 U.S. “Do Not Sell/Share” & GPC.</h4>
                    <p>For California (and other similar U.S. state laws), we provide a persistent “Do Not Sell or Share
                        My Personal Information” link and respect Global Privacy Control (GPC) signals. When a valid GPC
                        is detected for a California browser, we automatically:</p>
                    <p>Treat Advertising/Marketing as opt-out,</p>
                    <p>Disable any cross-context behavioral advertising tags, and</p>
                    <p>Display confirmation within the Cookie Settings panel.</p>
                    <p>This does not affect Strictly Necessary cookies.</p>
                    <h4>3.3 Rest of World.</h4>
                    <p>Outside the above regions, we provide the same granular controls. If local law allows implied
                        consent, we still present a banner and honor your explicit choices.</p>
                    <h4>3.4 Persistent “Cookie Settings.”</h4>
                    <p>A Cookie Settings control is permanently accessible (footer link across our Site and account menu
                        on app subdomains). You can modify or withdraw consent at any time. Changes apply prospectively;
                        we stop loading optional tags immediately upon withdrawal.</p>
                    <h4>3.5 Consent Duration & Renewal.</h4>
                    <p>We store your choices for 12 months (or shorter where mandated). We will re-prompt: (a) on
                        material policy or vendor changes; (b) if your region changes materially; or (c) when your
                        consent period expires.</p>
                    <h4>3.6 Minors.</h4>
                    <p>The Platform is 18+. If we discover use by a known minor, optional tracking is disabled and we
                        take appropriate steps per our <a href="/privacypolicy" target="_blank">Privacy Policy</a>.</p>

                    <h2>ARTICLE IV: COOKIE CATEGORIES & LEGAL BASES</h2>
                    <h4>4.1 Strictly Necessary.</h4>
                    <p>Purpose: Core site functions, security, network management, authentication, load balancing,
                        payment processing (e.g., Stripe fraud prevention & SCA), and consent storage.</p>
                    <p>Examples: session ID, CSRF token, consent state, payment fraud tokens, WAF/security edge cookies.
                    </p>
                    <p>Legal Basis: Legitimate interests (perform the service you request; keep it secure) and/or
                        contract necessity.</p>
                    <h4>4.2 Analytics/Performance (Optional).</h4>
                    <p>Purpose: Measure usage to improve reliability, features, and UX; error diagnostics; aggregated
                        performance metrics.</p>
                    <p>Provider: Google Analytics (GA4) configured to reduce data and respect consent.</p>
                    <p>Legal Basis: Consent (EU/UK) or legitimate interests/consent depending on region; we rely on
                        consent wherever prior consent is required.</p>
                    <h4>4.3 Advertising/Marketing (Optional).</h4>
                    <p>Purpose: Only if/when we run campaigns: measure campaign performance, limit frequency, and (if
                        you opt in and your region allows) enable interest-based ads.</p>
                    <p>Status by default: Off until you opt in.</p>
                    <p>Legal Basis: Consent (EU/UK); sale/share opt-out honored (U.S.).</p>
                    <h4>4.4 Device Fingerprinting / SDKs.</h4>
                    <p>We avoid device fingerprinting except where strictly necessary for fraud prevention or security
                        (e.g., Stripe’s anti-fraud signals). Any fingerprinting or SDK-based profiling for analytics or
                        advertising is treated as tracking and is gated by consent (EU/UK) or opt-out regimes (U.S.). We
                        do not use undisclosed covert identification.</p>

                    <h2>ARTICLE V: CONSENT MANAGEMENT PLATFORM (CMP)</h2>
                    <h4>5.1 Provider & Function.</h4>
                    <p>We use a third-party CMP integrated with our Site (the specific vendor may change). The CMP
                        displays the banner, stores your choices, prevents optional tags from firing before consent in
                        the EU/UK, and distributes your consent state to our tag manager.</p>
                    <h4>5.2 Consent Scope & Cross-Domain.</h4>
                    <p>Consent is applied across .cheapcomforts.com and designated subdomains (e.g.,
                        app.cheapcomforts.com, help.cheapcomforts.com) using first-party consent cookies and CMP APIs.
                        If we add new subdomains or domains, we will extend the scope and note it in Cookie Settings.
                    </p>
                    <h4>5.3 Signals & Sync.</h4>
                    <p>The CMP records granular category choices and, where possible, honors GPC automatically. For U.S.
                        state privacy laws, your opt-out is translated into suppressed data flows for any vendors tagged
                        as “sale/share” participants.</p>
                    <h4>5.4 Records.</h4>
                    <p>Consent logs are retained for up to 24 months (or the period legally required) and then
                        pseudonymized or deleted. Logs are used solely to prove compliance, audit vendor firing, and
                        diagnose user-reported issues.</p>

                    <h2>ARTICLE VI: GOOGLE ANALYTICS CONFIGURATION (STRICT)</h2>
                    <h4>6.1 GA4 Default Settings.</h4>
                    <p>IP anonymization enabled or IP collection disabled to the extent supported.</p>
                    <p>EU traffic processed with region-based controls to keep optional features off until consent.</p>
                    <p>Data retention set to 14 months for event data (or a shorter period if required regionally).</p>
                    <p>Google signals and ad personalization disabled by default; only enabled if you opt into
                        Advertising/Marketing.</p>
                    <p>Granular location & device data minimized where feasible.</p>
                    <p>No advertising identifiers collected unless you opt in to Advertising/Marketing.</p>
                    <h4>6.2 Respecting Consent.</h4>
                    <p>If you reject Analytics, GA4 is not loaded (EU/UK) or is throttled/suppressed (other regions) via
                        the CMP and tag manager. If you later withdraw consent, Analytics collection stops and pending
                        tags are disabled prospectively.</p>
                    <h4>6.3 Data Sharing & Transfers.</h4>
                    <p>We use contractual safeguards for international transfers (e.g., standard contractual clauses)
                        via our processors. See our <a href="/privacypolicy" target="_blank">Privacy Policy</a> for
                        details.</p>

                    <h2>ARTICLE VII: STRIPE PAYMENTS & ESSENTIAL COOKIES</h2>
                    <h4>7.1 Stripe Fraud & SCA.</h4>
                    <p>Stripe sets strictly necessary cookies and uses device signals to detect fraud and perform Strong
                        Customer Authentication where applicable. Because these functions are essential to process your
                        monthly recurring charges, they are categorized as Strictly Necessary.</p>
                    <h4>7.2 No Optional Overlays.</h4>
                    <p>Stripe is required to process bookings and defend against fraud/SCA. They are Strictly Necessary.
                    </p>

                    <h2>ARTICLE VIII: HOSTING / SECURITY (HOSTINGER) & EDGE COOKIES</h2>
                    <h4>8.1 Security & Performance.</h4>
                    <p>Our hosting provider (Hostinger) and any edge/CDN/WAF layers used for availability and security
                        may place Strictly Necessary cookies (e.g., to route traffic, defend against bots, implement
                        rate-limiting, or maintain session affinity).</p>
                    <h4>8.2 Variable Names.</h4>
                    <p>These security cookies may have provider-specific names that change over time. They are
                        classified as Strictly Necessary and do not require consent.</p>

                    <h2>ARTICLE IX: ADVERTISING/MARKETING (IF YOU OPT IN)</h2>
                    <h4>9.1 What May Load If Enabled.</h4>
                    <p>If—and only if—you opt in to the Advertising/Marketing category (and your region permits), we may
                        enable:</p>
                    <p>Conversion tags to measure ad campaign effectiveness,</p>
                    <p>Frequency capping and reach,</p>
                    <p>Remarketing lists only if you consent to interest-based ads.</p>
                    <h4>9.2 U.S. Opt-Out.</h4>
                    <p>For California and similar U.S. states, the “Do Not Sell/Share” link (and any GPC signal) will
                        disable cross-context advertising flows regardless of your prior cookie choices, unless you
                        explicitly choose otherwise where allowed by law.</p>
                    <h4>9.3 Email Marketing.</h4>
                    <p>Email subscriptions require their own consent (separate from cookies). Analytics/Marketing
                        cookies do not equate to email consent.</p>

                    <h2>ARTICLE X: DEVICE FINGERPRINTING & MOBILE SDKS</h2>
                    <h4>10.1 Fraud/Security Only Without Opt-In.</h4>
                    <p>We avoid device fingerprinting except where strictly necessary for fraud prevention or security
                        (e.g., Stripe’s anti-fraud signals). This is Strictly Necessary. We do not deploy covert
                        fingerprinting for advertising without your explicit consent.</p>
                    <h4>10.2 Mobile SDK Parity.</h4>
                    <p>If we release a mobile app, SDKs will reflect the same categories: Strictly Necessary (app
                        operations, crash logs), Analytics (usage metrics, disabled until opt-in where required).</p>

                    <h2>ARTICLE XI: COOKIE TABLE</h2>
                    <table border="1">
                        <thead>
                            <tr>
                                <th>Cookie Name</th>
                                <th>Provider</th>
                                <th>Category</th>
                                <th>Purpose</th>
                                <th>Type</th>
                                <th>Duration</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>cheapcomforts_session</td>
                                <td>Cheap Comforts</td>
                                <td>Strictly Necessary</td>
                                <td>Session management and authentication after login.</td>
                                <td>1st-party</td>
                                <td>Session</td>
                            </tr>
                            <tr>
                                <td>cc_consent</td>
                                <td>Cheap Comforts (CMP)</td>
                                <td>Strictly Necessary</td>
                                <td>Stores your consent categories and timestamp; prevents optional tags from firing in
                                    EU/UK before opt-in.</td>
                                <td>1st-party</td>
                                <td>12 months</td>
                            </tr>
                            <tr>
                                <td>cc_region</td>
                                <td>Cheap Comforts (CMP)</td>
                                <td>Strictly Necessary</td>
                                <td>Records regional consent regime (EU/UK/US/ROW) to apply correct banner.</td>
                                <td>1st-party</td>
                                <td>12 months</td>
                            </tr>
                            <tr>
                                <td>csrftoken</td>
                                <td>Cheap Comforts</td>
                                <td>Strictly Necessary</td>
                                <td>Protects forms and sessions from CSRF attacks.</td>
                                <td>1st-party</td>
                                <td>12 months</td>
                            </tr>
                            <tr>
                                <td>__stripe_mid</td>
                                <td>Stripe</td>
                                <td>Strictly Necessary</td>
                                <td>Fraud prevention and device recognition for payments.</td>
                                <td>3rd-party</td>
                                <td>12 months</td>
                            </tr>
                            <tr>
                                <td>__stripe_sid</td>
                                <td>Stripe</td>
                                <td>Strictly Necessary</td>
                                <td>Session-level anti-fraud and SCA workflows.</td>
                                <td>3rd-party</td>
                                <td>Session</td>
                            </tr>
                            <tr>
                                <td>_ga</td>
                                <td>Google Analytics</td>
                                <td>Analytics/Performance</td>
                                <td>Distinguishes users for analytics (pseudonymous ID). Not set until opt-in in EU/UK.
                                </td>
                                <td>1st-party</td>
                                <td>13 months (EU/UK) / up to 24 months (elsewhere)</td>
                            </tr>
                            <tr>
                                <td>ga*</td>
                                <td>Google Analytics</td>
                                <td>Analytics/Performance</td>
                                <td>Stores session state with GA4. Not set until opt-in in EU/UK.</td>
                                <td>1st-party</td>
                                <td>13–24 months (region-dependent)</td>
                            </tr>
                            <tr>
                                <td>_gid</td>
                                <td>Google Analytics</td>
                                <td>Analytics/Performance</td>
                                <td>Distinguishes sessions for analytics reporting.</td>
                                <td>1st-party</td>
                                <td>24 hours</td>
                            </tr>
                            <tr>
                                <td>_gcl_au</td>
                                <td>Google (Ads)</td>
                                <td>Advertising/Marketing</td>
                                <td>Ad conversion tracking/attribution. Only if opted in; disabled with GPC/Do-Not-Sell.
                                </td>
                                <td>1st-party</td>
                                <td>90 days</td>
                            </tr>
                            <tr>
                                <td>_uetsid / _uetvid</td>
                                <td>Microsoft Ads (if enabled)</td>
                                <td>Advertising/Marketing</td>
                                <td>Ad campaign measurement/remarketing.</td>
                                <td>1st/3rd-party</td>
                                <td>1 day / 13 months</td>
                            </tr>
                            <tr>
                                <td>remember_user_token</td>
                                <td>Cheap Comforts</td>
                                <td>Strictly Necessary</td>
                                <td>Keeps you logged in if you choose “remember me.”</td>
                                <td>1st-party</td>
                                <td>Up to 30 days</td>
                            </tr>
                            <tr>
                                <td>waf_session / edge_route</td>
                                <td>Hosting/CDN</td>
                                <td>Strictly Necessary</td>
                                <td>Load balancing, bot mitigation, and routing.</td>
                                <td>1st/3rd-party</td>
                                <td>Session/Short-term</td>
                            </tr>
                        </tbody>
                    </table>
                    <br>
                    <p>The Cookie Settings panel shows the current, authoritative list with purposes and durations,
                        including any new vendors we introduce.</p>

                    <h2>ARTICLE XII: HOW YOU CAN CONTROL COOKIES</h2>
                    <h4>12.1 On-Site Controls (Preferred).</h4>
                    <p>Use the Cookie Settings control at any time to:</p>
                    <p>Toggle Analytics/Performance and Advertising/Marketing,</p>
                    <p>Reject all optional cookies,</p>
                    <p>Withdraw previous consent, or</p>
                    <p>Review the live cookie table and vendor details.</p>
                    <h4>12.2 Browser Controls.</h4>
                    <p>You can block or delete cookies in your browser settings. Doing so may break functionality (e.g.,
                        login persistence, checkout). Browser controls do not always cover local storage or SDK
                        settings, so use on-site controls first.</p>
                    <h4>12.3 Mobile App Controls.</h4>
                    <p>If using our mobile app (now or in the future), use the in-app Privacy/Tracking settings and your
                        OS settings (e.g., “Allow Apps to Request to Track” on iOS, Advertising ID on Android).</p>
                    <h4>12.4 GPC / Do-Not-Track.</h4>
                    <p>We honor GPC for California “sale/share” opt-outs. Standard “Do Not Track” (DNT) signals are not
                        consistently standardized; we rely on GPC for enforceable web privacy signals.</p>
                    <h4>12.5 Email/Push Marketing.</h4>
                    <p>Email and push notifications require separate consent or lawful basis and have their own
                        unsubscribe mechanisms. Cookie preferences do not change email subscriptions.</p>

                    <h2>ARTICLE XIII: DATA MINIMIZATION, RETENTION & DELETION</h2>
                    <h4>13.1 Minimization.</h4>
                    <p>We only deploy the minimum cookies needed for the stated purposes. We avoid device fingerprinting
                        except where strictly necessary for fraud prevention or security (e.g., Stripe’s anti-fraud
                        signals). This is Strictly Necessary. We do not deploy covert fingerprinting for advertising
                        without your explicit consent.</p>
                    <h4>13.2 Cookie Lifetimes.</h4>
                    <p>We set conservative durations (see table). Where vendors offer shorter retention variants, we
                        select them for stricter regions (e.g., 13 months for GA4 IDs in the EU/UK).</p>
                    <h4>13.3 Consent Logs.</h4>
                    <p>Retained up to 24 months (or as required) and then deleted or pseudonymized. Logs are not used to
                        profile you; they evidence your choices.</p>
                    <h4>13.4 Withdrawal & Deletion.</h4>
                    <p>Withdrawing Analytics or Advertising stops future collection. You may also request deletion of
                        analytics identifiers under applicable privacy laws—see <a href="/privacypolicy"
                            target="_blank">Privacy Policy</a> for rights and process.
                    </p>

                    <h2>ARTICLE XIV: INTERNATIONAL TRANSFERS</h2>
                    <h4>14.1 Processors & Transfers.</h4>
                    <p>Our vendors (e.g., Stripe, Google) may process cookie-derived data outside your jurisdiction. We
                        rely on appropriate transfer mechanisms (e.g., Standard Contractual Clauses, adequacy decisions,
                        or equivalent safeguards) as described in the <a href="/privacypolicy" target="_blank">Privacy
                            Policy</a>.</p>
                    <h4>14.2 Regional Processing.</h4>
                    <p>For EU/UK visitors, we apply region-based processing controls to reduce cross-border transfers
                        for optional features unless consented.</p>

                    <h2>ARTICLE XV: SECURITY & FRAUD PREVENTION</h2>
                    <h4>15.1 Essential Security Cookies.</h4>
                    <p>Session IDs, CSRF tokens, and anti-bot cookies help secure your account and payment flows. These
                        are Strictly Necessary.</p>
                    <h4>15.2 Abuse Prevention.</h4>
                    <p>We may temporarily deploy additional Strictly Necessary protective cookies during suspected
                        attack windows (e.g., credential stuffing, scraping, DDoS). These do not require consent.</p>

                    <h2>ARTICLE XVI: INTERPLAY WITH PRIVACY RIGHTS</h2>
                    <h4>16.1 EU/UK Rights.</h4>
                    <p>Access, erasure, restriction, portability, and objection rights may apply to personal data
                        collected via cookies. Where consent is the basis, you may withdraw consent at any time via
                        Cookie Settings.</p>
                    <h4>16.2 U.S. State Rights (e.g., CA).</h4>
                    <p>You may exercise Do Not Sell/Share from the footer link or via GPC. You may also request to know,
                        delete, or correct personal information as set out in our <a href="/privacypolicy"
                            target="_blank">Privacy Policy</a>.</p>
                    <h4>16.3 Brazil (LGPD).</h4>
                    <p>You may request confirmation of processing, access, correction, anonymization, deletion,
                        portability, and revocation of consent. Contact <a href="mailto:support@cheapcomforts.com"
                            target="_blank">support@cheapcomforts.com</a>.</p>

                    <h2>ARTICLE XVII: ENFORCEMENT, DISPUTES & LIMITATIONS</h2>
                    <h4>17.1 Enforcement.</h4>
                    <p>We log consent and tag behavior to ensure optional scripts do not fire before consent where
                        required. We also periodically audit vendor configurations.</p>
                    <h4>17.2 Disputes.</h4>
                    <p>Cookie-related disputes follow the process and governing law in our <a href="/termsofservice"
                            target="_blank">Terms of Service</a> (Spain),
                        without prejudice to mandatory local consumer/data rules.</p>
                    <h4>17.3 Limitations & Availability.</h4>
                    <p>We strive for accuracy in the live cookie table, but vendors may change cookie names and
                        behaviors. The Cookie Settings panel is authoritative and updated in near real time by the CMP.
                    </p>

                    <h2>ARTICLE XVIII: FREQUENTLY ASKED QUESTIONS (GUIDANCE)</h2>
                    <h4>18.1 Why do I see the banner again?</h4>
                    <p>We re-prompt when: (i) your region changes; (ii) your consent has expired; (iii) we materially
                        change vendors/purposes; or (iv) your browser blocks the consent cookie.</p>
                    <h4>18.2 Can I use the site if I reject all optional cookies?</h4>
                    <p>Yes. Core functionality is supported by Strictly Necessary cookies only. Some analytics-powered
                        features may be limited.</p>
                    <h4>18.3 Do you sell my data?</h4>
                    <p>We do not sell personal information in the traditional sense. Where U.S. laws define “sale/share”
                        to include cross-context advertising, we provide a Do Not Sell/Share control and honor GPC, and
                        we default Advertising/Marketing to off unless you opt in.</p>
                    <h4>18.4 Are Stripe cookies optional?</h4>
                    <p>No. Stripe is required to process bookings and defend against fraud/SCA. They are Strictly
                        Necessary.</p>
                    <h4>18.5 Do you use hidden cameras or device tracking?</h4>
                    <p>Hidden cameras are prohibited by our marketplace rules. Device fingerprinting for marketing is
                        off unless you opt in; minimal fraud prevention signals may be used as Strictly Necessary.</p>

                    <h2>ARTICLE XIX: DETAILED CATEGORY DESCRIPTIONS & EXAMPLES</h2>
                    <h4>19.1 Strictly Necessary.</h4>
                    <p>Authentication & Session: maintain your logged-in state (e.g., cheapcomforts_session,
                        remember_user_token).</p>
                    <p>Security: prevent cross-site request forgery and brute force (csrftoken, edge/WAF cookies).</p>
                    <p>Consent Storage: remember your cookie choices across visits (cc_consent).</p>
                    <p>Payments: Stripe fraud detection (__stripe_mid, __stripe_sid).</p>
                    <p>Impact if disabled: Site cannot function securely or complete bookings.</p>
                    <h4>19.2 Analytics/Performance.</h4>
                    <p>Traffic & Navigation: pageviews, time on page, feature usage (_ga, _ga_*, _gid).</p>
                    <p>Errors: crash and performance diagnostics (event counts).</p>
                    <p>Product Improvement: aggregated metrics for improving search, calendar, and checkout flows.</p>
                    <p>Impact if disabled: We lose insight into usage patterns; your experience remains functional.</p>
                    <h4>19.3 Advertising/Marketing.</h4>
                    <p>Conversion Measurement: measure ad campaign effectiveness (_gcl_au).</p>
                    <p>Remarketing: show relevant ads elsewhere (only if opted in and where allowed).</p>
                    <p>Frequency Capping: limit how often you see an ad.</p>
                    <p>Impact if disabled: You still see ads, but they will not be personalized using our tags.</p>

                    <h2>ARTICLE XX: IMPLEMENTATION DETAILS (FOR TRANSPARENCY)</h2>
                    <h4>20.1 Tag Firing Rules.</h4>
                    <p>EU/UK visitors: optional categories blocked by default; tags fire only after opt-in.</p>
                    <p>U.S. visitors: Do Not Sell/Share disables cross-context ad tags; Analytics follows your cookie
                        choice.</p>
                    <p>Everywhere: Strictly Necessary always allowed.</p>
                    <h4>20.2 Tag Manager.</h4>
                    <p>We use a tag manager with consent checks. It will not deploy optional tags if the CMP indicates
                        no consent. If you withdraw consent, it halts future loads.</p>
                    <h4>20.3 Server-Side / Proxy.</h4>
                    <p>Where we use server-side tagging or a proxy, we apply the same consent checks and filter out
                        ad/analytics events until consent exists.</p>
                    <h4>20.4 Breakage & Debugging.</h4>
                    <p>If you experience breakage after changing cookie settings, try clearing cookies for our domain
                        and reloading the page; then reapply your choices via Cookie Settings.</p>

                    <h2>ARTICLE XXI: SPECIFIC PROVIDERS MENTIONED</h2>
                    <h4>21.1 Hostinger (Hosting).</h4>
                    <p>May place short-lived security or routing cookies categorized as Strictly Necessary.</p>
                    <h4>21.2 Stripe (Payments).</h4>
                    <p>Places Strictly Necessary cookies for payment authorization, anti-fraud, and SCA.</p>
                    <h4>21.3 Google Analytics (Measurement).</h4>
                    <p>Classified under Analytics/Performance; disabled until opt-in in EU/UK; configured with
                        privacy-preserving settings as described in Article VI; ad features off unless you opt into
                        Advertising/Marketing.</p>

                    <h2>ARTICLE XXII: RESPONSIBLE USE & BUSINESS PROTECTIONS</h2>
                    <h4>22.1 Favoring Security & Compliance.</h4>
                    <p>We prioritize security, fraud prevention, and legal compliance. If a conflict arises between user
                        preferences and platform security/legal obligations, Strictly Necessary controls prevail to keep
                        accounts and transactions safe.</p>
                    <h4>22.2 No Waiver of Rights.</h4>
                    <p>Nothing in this Policy limits Cheap Comforts’ rights under the <a href="/termsofservice"
                            target="_blank">Terms of Service</a>, including
                        content moderation, incident response, or suspension for abuse of preferences (e.g., attempting
                        to circumvent security via cookie manipulation).</p>
                    <h4>22.3 Limitation.</h4>
                    <p>To the fullest extent permitted by law, we are not liable for third-party changes to cookie
                        behaviors, browser bugs, or vendor outages. Remedies are limited to those set out in the <a
                            href="/termsofservice" target="_blank">Terms of Service</a>.</p>

                    <h2>ARTICLE XXIII: HOW TO REACH US</h2>
                    <h4>23.1 Contact.</h4>
                    <p>For any cookie-related request, including access, deletion of identifiers, or questions about
                        this Policy, email <a href="mailto:support@cheapcomforts.com"
                            target="_blank">support@cheapcomforts.com</a>.</p>
                    <h4>23.2 Effective Date.</h4>
                    <p>This Policy is effective as of 7 September 2025 and supersedes prior versions.</p>

                    <h2>ACCEPTANCE & CONTROL</h2>
                    <p>By continuing to use cheapcomforts.com, you acknowledge that you have had the opportunity to
                        accept, reject, or customize non-essential cookies through our banner and Cookie Settings. You
                        can change your mind at any time. Strictly necessary technologies remain in place to provide a
                        secure, functional marketplace experience.</p>
                </div>
            </div>
        </section>
    </div>
</template>
<script>
export default {
    name: 'cookiesPolicy',
    data() {
        return {};
    },
    mounted() { },
    methods: {},
    computed: {},
    created() { }
}
</script>